import ChapterBadge from "./ChapterBadge";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { 
  Compass,
  MapTrifold,
  ChartBar,
  CheckSquare,
  Gauge,
  Rocket
} from "@phosphor-icons/react";

const HowItWorks = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const steps = [
    {
      icon: Compass,
      title: "Discovery",
      description: "Map systems, teams, reports, and knowledge sources across your enterprise.",
    },
    {
      icon: MapTrifold,
      title: "Source Mapping",
      description: "Create a unified map of structured systems and institutional memory sources.",
    },
    {
      icon: ChartBar,
      title: "KPI + Memory Analysis",
      description: "Infer likely metrics, entities, questions, and reusable knowledge from existing data.",
    },
    {
      icon: CheckSquare,
      title: "Answerability",
      description: "Classify what is immediately answerable, what is derivable, and what is blocked.",
    },
    {
      icon: Gauge,
      title: "Readiness",
      description: "Generate an executive intelligence and enterprise memory readiness picture.",
    },
    {
      icon: Rocket,
      title: "Activate",
      description: "Deploy OnTenacity as a daily brief and enterprise memory layer for your organization.",
    },
  ];

  return (
    <section 
      id="how-it-works"
      ref={ref}
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 relative"
      data-testid="how-it-works-section"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#0A0A0B]" />

      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <ChapterBadge number="08" name="Workflow" />
          <span className="mono-label">Process</span>
          <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-white font-['Outfit']">
            How OnTenacity works
          </h2>
          <p className="mt-4 text-[#A1A1AA] max-w-xl mx-auto text-base md:text-lg">
            A structured journey from discovery to deployment.
          </p>
        </motion.div>

        {/* Stepper */}
        <div className="relative" data-testid="stepper-container">
          {/* Vertical Line */}
          <div className="absolute left-6 md:left-8 top-0 bottom-0 w-px bg-gradient-to-b from-[#00E5FF] via-[#06B6D4] to-transparent" />

          {/* Steps */}
          <div className="space-y-8 md:space-y-12">
            {steps.map((step, index) => (
              <motion.div
                key={step.title}
                initial={{ opacity: 0, x: -20 }}
                animate={isInView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative flex gap-6 md:gap-8"
                data-testid={`step-${step.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {/* Step Icon */}
                <div className="relative z-10 flex-shrink-0">
                  <div className="w-12 h-12 md:w-16 md:h-16 rounded-xl bg-[#0F0F10] border border-white/10 flex items-center justify-center group hover:border-[#00E5FF]/50 transition-colors">
                    <step.icon 
                      size={24} 
                      weight="duotone" 
                      className="text-[#00E5FF] md:w-7 md:h-7" 
                    />
                  </div>
                  {/* Connector Dot */}
                  <div className="absolute -left-[3px] top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#00E5FF] connector-dot" style={{ animationDelay: `${index * 0.3}s` }} />
                </div>

                {/* Step Content */}
                <div className="pt-2 md:pt-4">
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-xs text-[#00E5FF] font-mono">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <h3 className="text-xl md:text-2xl font-semibold text-white font-['Outfit']">
                      {step.title}
                    </h3>
                  </div>
                  <p className="text-[#A1A1AA] text-sm md:text-base leading-relaxed max-w-md">
                    {step.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;
